import React, { useState, useEffect } from 'react';
import api from './api';
import TarBackupPanel from './TarBackupPanel';

// ── Backup status modal (restic two-repo system) ─────────────────────────────
// Consumes GET /files/admin/backup-status. The BACKEND computes every badge
// (green/amber/red) and the dual freshness gate; this component only renders the
// colors + reasons it is given. It does NOT recompute badge logic.

const BADGE_COLORS = { green: '#3B6D11', amber: '#BA7517', red: '#A32D2D' };
const badgeColor = (b) => BADGE_COLORS[b] || '#888';  // unknown/missing -> grey

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined || bytes < 0) return '—';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 B';
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const formatNumber = (n) => (n === null || n === undefined ? '—' : n.toLocaleString());

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const formatAge = (hours) => {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 24) return `${hours.toFixed(1)}h ago`;
  const d = Math.floor(hours / 24), h = Math.round(hours % 24);
  return `${d}d ${h}h ago`;
};

function Dot({ badge, size = 11 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%',
      background: badgeColor(badge), flexShrink: 0, display: 'inline-block',
    }} />
  );
}

export default function BackupStatusModal({ darkMode, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bg = darkMode ? '#1a1a1a' : '#ffffff';
  const panelBg = darkMode ? '#242424' : '#f5f5f5';
  const subPanelBg = darkMode ? '#1f1f1f' : '#eeeeee';
  const borderColor = darkMode ? '#333' : '#ddd';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/files/admin/backup-status');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stale = !!data && (data.overall_state === 'stale' || data.ok === false);
  const helperErrors = (data && data.helper_errors) || [];

  // Group the target rows by repo; render QNAP before UNAS within each.
  // 5.35.0: HDCal (Davis calendar, 10.0.9.9) — pulled nightly at 23:00, copied to the UNAS in the same run.
  const repoOrder = [
    { repo: 'insuredocs-system', label: 'System repo', sub: 'pg_dump + /opt configs + /etc' },
    { repo: 'insuredocs-docsvault', label: 'Docsvault repo', sub: 'docsvault_raw/ (~257 GiB)' },
    { repo: 'hdcal', label: 'HDCal', sub: 'Davis calendar server 10.0.9.9 — pulled nightly 23:00' },
  ];
  const rowsFor = (repo) => (data?.targets || [])
    .filter(t => t.repo === repo)
    .sort((a, b) => (a.destination === 'qnap' ? 0 : 1) - (b.destination === 'qnap' ? 0 : 1));

  const TargetRow = ({ t }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: `1px solid ${borderColor}` }}>
      <div style={{ paddingTop: 2 }} title={(t.badge_reasons || []).join(' · ')}><Dot badge={t.badge} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{t.destination_label || t.destination}</span>
          <span style={{ fontSize: 11, color: textSecondary }} title={formatTimestamp(t.snapshot?.time)}>
            {t.overdue ? '⚠ ' : ''}{formatAge(t.age_hours)}
          </span>
        </div>
        <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
          last snapshot {formatTimestamp(t.snapshot?.time)}
          {t.snapshot?.short_id ? ` · ${t.snapshot.short_id}` : ''}
          {' · '}{t.repo === 'hdcal' ? '1 export tar' : `${formatNumber(t.snapshot?.total_file_count)} files`} · {formatBytes(t.snapshot?.total_size_bytes)}
        </div>
        {/* badge_reasons: always available on hover (dot title); shown inline when not green */}
        {t.badge !== 'green' && (t.badge_reasons || []).length > 0 && (
          <div style={{ fontSize: 11, color: badgeColor(t.badge), marginTop: 3 }}>
            {(t.badge_reasons || []).join(' · ')}
          </div>
        )}
        {t.status_file?.detail && t.repo === 'hdcal' && (
          <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
            last run: {t.status_file.verdict || '—'} {formatTimestamp(t.status_file.time)} · {t.status_file.detail}
          </div>
        )}
        {t.error && (
          <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 3, fontFamily: 'monospace' }}>{t.error}</div>
        )}
      </div>
    </div>
  );

  const rt = data?.restore_test;
  const guard = data?.docsvault_guard;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 60,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 720, maxHeight: '80vh',
        background: bg, color: textPrimary, border: `1px solid ${borderColor}`,
        borderRadius: 10, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Backups</div>
            <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
              nightly restic + one-click encrypted backups (InsureDocs, Mattermost, Timeclock, HDCal) — QNAP (on-site) + UNAS (off-site)
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={load} disabled={loading} style={{ background: 'none', border: `1px solid ${borderColor}`, color: textSecondary, fontSize: 12, padding: '4px 10px', borderRadius: 4, cursor: loading ? 'wait' : 'pointer' }}>
              {loading ? 'Loading…' : '↻ Refresh'}
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer', padding: 0 }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          {loading && !data && (
            <div style={{ color: textSecondary, fontSize: 13, textAlign: 'center', padding: 40 }}>Loading backup status…</div>
          )}
          {error && (
            <div style={{ padding: '10px 12px', background: '#FCEBEB', color: '#A32D2D', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>Error: {error}</div>
          )}

          {data && (
            <>
              {/* STALE banner — the "helper not reporting" surface. Unmissable. */}
              {stale && (
                <div style={{ padding: '12px 14px', background: '#FBF0D9', color: '#7A5200', border: '1px solid #BA7517', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>⚠ {data.banner || 'status data stale — helper not reporting'}</div>
                  {data.reason && <div style={{ marginTop: 4, fontSize: 12 }}>{data.reason}</div>}
                  <div style={{ marginTop: 4, fontSize: 12 }}>The rows below are the last cached values and are NOT confirmed live.</div>
                </div>
              )}

              {/* Helper-level errors — surface even one. */}
              {helperErrors.length > 0 && (
                <div style={{ padding: '10px 12px', background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Helper reported {helperErrors.length} error{helperErrors.length > 1 ? 's' : ''}:</div>
                  {helperErrors.map((e, i) => (<div key={i} style={{ fontFamily: 'monospace', fontSize: 11 }}>• {e}</div>))}
                </div>
              )}

              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: textSecondary, margin: '0 0 8px 2px' }}>
                NIGHTLY (RESTIC) — QNAP + UNAS (Tailscale)
              </div>
              {/* Rows de-emphasized when stale (not live). */}
              <div style={{ opacity: stale ? 0.55 : 1 }}>
                {repoOrder.map(({ repo, label, sub }) => (
                  <div key={repo} style={{ background: panelBg, borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
                      <span style={{ fontSize: 11, color: textSecondary }}>{sub}</span>
                    </div>
                    {rowsFor(repo).length === 0
                      ? <div style={{ fontSize: 12, color: textSecondary, paddingTop: 8 }}>No data.</div>
                      : rowsFor(repo).map(t => <TargetRow key={t.key} t={t} />)}
                  </div>
                ))}

                {/* Two cards: restore-test + docsvault guard */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {/* Restore test */}
                  <div style={{ background: panelBg, borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <Dot badge={rt?.badge} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Restore test</span>
                    </div>
                    {rt ? (
                      <div style={{ fontSize: 12 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: badgeColor(rt.badge) }}>{rt.verdict || '—'}</div>
                        <div style={{ color: textSecondary, marginTop: 2 }} title={formatTimestamp(rt.time)}>
                          {rt.overdue ? '⚠ ' : ''}{formatAge(rt.age_hours)}
                        </div>
                        {rt.detail && <div style={{ color: textSecondary, marginTop: 4 }}>{rt.detail}</div>}
                        {rt.badge !== 'green' && (rt.badge_reasons || []).length > 0 && (
                          <div style={{ color: badgeColor(rt.badge), marginTop: 4 }}>{(rt.badge_reasons || []).join(' · ')}</div>
                        )}
                      </div>
                    ) : <div style={{ fontSize: 12, color: textSecondary }}>No data.</div>}
                  </div>

                  {/* Docsvault file-count guard */}
                  <div style={{ background: panelBg, borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <Dot badge={guard?.badge} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Docsvault guard</span>
                    </div>
                    {guard ? (
                      <div style={{ fontSize: 12 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: badgeColor(guard.badge) }}>
                          {formatNumber(guard.current_file_count)} files
                        </div>
                        <div style={{ color: textSecondary, marginTop: 2 }}>
                          baseline {formatNumber(guard.baseline_last_good)} · floor {formatNumber(guard.floor)}
                        </div>
                        <div style={{ color: textSecondary, marginTop: 2 }}>
                          Δ {guard.delta_vs_baseline >= 0 ? '+' : ''}{formatNumber(guard.delta_vs_baseline)}
                          {guard.pct_change !== null && guard.pct_change !== undefined ? ` (${guard.pct_change}%)` : ''}
                          {' · '}state: <span style={{ color: badgeColor(guard.badge), fontWeight: 600 }}>{guard.state}</span>
                        </div>
                        <div style={{ color: textSecondary, marginTop: 2 }}>
                          drops: {guard.consecutive_drop_count} / 2 · measured {formatAge(guard.age_hours)}
                        </div>
                        {guard.badge !== 'green' && (guard.badge_reasons || []).length > 0 && (
                          <div style={{ color: badgeColor(guard.badge), marginTop: 4 }}>{(guard.badge_reasons || []).join(' · ')}</div>
                        )}
                      </div>
                    ) : <div style={{ fontSize: 12, color: textSecondary }}>No data.</div>}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* One-click encrypted backups (tarbackup: InsureDocs, Mattermost 5.25.0, Timeclock 5.29.0, HDCal 5.35.0) — independent of the restic status above */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: textSecondary, margin: '18px 0 0 2px', borderTop: `1px solid ${borderColor}`, paddingTop: 12 }}>
            ONE-CLICK (MANUAL)
          </div>
          <TarBackupPanel darkMode={darkMode} />
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 18px', borderTop: `1px solid ${borderColor}`, fontSize: 11, color: textSecondary, textAlign: 'center', flexShrink: 0 }}>
          {data
            ? <>Status generated {formatTimestamp(data.generated_at)}
                {data.age_seconds !== null && data.age_seconds !== undefined ? ` (${Math.round(data.age_seconds)}s ago)` : ''}
                {' · '}polled every {Math.round((data.helper_interval_seconds || 900) / 60)} min by insuredocs-backup-status.timer
                {data.host ? ` · ${data.host}` : ''}</>
            : 'Reads /opt/insuredocs/logs/restic_status.json (maintained by the status timer).'}
        </div>
      </div>
    </div>
  );
}
